import { PokeForm } from "@app/components/poke/shadcn/ui/form";
import {
  InputField,
  SelectField,
} from "@app/components/poke/shadcn/ui/form/fields";
import type { SwitchContractBodySchema } from "@app/lib/api/poke/switch_contract";
import { clientFetch } from "@app/lib/egress/client";
import { amountCents } from "@app/lib/metronome/amounts";
import { isPaygEligibleTier } from "@app/lib/metronome/types";
import {
  CREDIT_PRICED_BUSINESS_PLAN_CODE,
  CREDIT_PRICED_FREE_PLAN_CODE,
  isEnterprisePlanPrefix,
  PRO_PLAN_SEAT_29_CODE,
  PRO_PLAN_SEAT_39_CODE,
} from "@app/lib/plans/plan_codes";
import { useAppRouter } from "@app/lib/platform";
import {
  usePokeMetronomePackages,
  usePokePlans,
  usePokeStripeCustomerCurrency,
} from "@app/lib/swr/poke";
import assert from "@app/lib/utils/assert";
import { usePokePluginAsyncArgs } from "@app/poke/swr/plugins";
import { isCreditPricedPlan } from "@app/types/plan";
import { CreditUsageConfigurationSchema } from "@app/types/poke/credit_usage_configuration";
import type { WorkspaceType } from "@app/types/user";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  SliderToggle,
  Spinner,
} from "@dust-tt/sparkle";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const SwitchContractFormSchema = z
  .object({
    metronomePackageId: z.string().min(1, "Required"),
    planCode: z.string().min(1, "Required"),
    hubspotDealId: z.string().optional(),
    startingAt: z.string().optional(),
    // How the enterprise contract's start moment is resolved:
    //  - "immediately": swap at the current hour (no startingAt sent).
    //  - "retroactive_first_of_month": backdate to the 1st of the current month,
    //    00:00 UTC.
    //  - "select": use the operator-chosen `startingAt` (the only mode that
    //    surfaces the date picker).
    startMode: z
      .enum(["immediately", "retroactive_first_of_month", "select"])
      .default("select"),
    stripeCustomerId: z.string(),
    stripeCollectionMethod: z
      .enum(["charge_automatically", "send_invoice"])
      .default("charge_automatically"),
    // Net payment terms in days (e.g. 30 for "Net 30"). Only relevant for
    // `send_invoice`; empty leaves Metronome's account default in place.
    netPaymentTermsDays: z
      .number()
      .int("Net payment terms must be a whole number of days")
      .min(0, "Net payment terms must be ≥ 0")
      .max(365, "Net payment terms must be ≤ 365")
      .optional(),
    // Credit usage configuration.
    paygEnabled: z.boolean().default(false),
    usageCapCredits: z
      .number()
      .int("Usage cap must be an integer number of credits")
      .min(1, "Usage cap must be at least 1 credit")
      .optional(),
    defaultDiscountPercent: z.number().int().min(0).max(100).default(0),
    balanceThresholdCredits: z.number().int().min(0).optional(),
    defaultPoolCapCredits: z.number().int().min(0).optional(),
    programmaticMonthlyCapCredits: z.number().int().min(0).optional(),
    autoSeatUpgradeEnabled: z.boolean().default(false),
    topUpEnabled: z.boolean().default(false),
    autoInvoiceFinalizationEnabled: z.boolean().default(true),
    // One-off initial credits (contract-level prepaid commit). Toggled on via
    // `showInitialCredits`; both fields are then required together and assembled
    // into `initialCredits` on submit.
    showInitialCredits: z.boolean().default(false),
    initialCreditsAmount: z
      .number()
      .int("Initial credits must be an integer number of credits")
      .min(1, "Initial credits must be at least 1 credit")
      .optional(),
    initialCreditsInvoiceAmount: z
      .number()
      .min(0, "Invoice amount must be zero or more")
      .optional(),
    initialCreditsFrequency: z
      .enum(["one_time", "monthly", "quarterly", "semi_annually", "annually"])
      .default("one_time"),
    initialCreditsPeriods: z
      .number()
      .int("Number of periods must be a whole number")
      .min(2, "Number of periods must be at least 2")
      .max(60, "Number of periods must be at most 60")
      .optional(),
    // Per-seat-type settings, keyed by seat type. Every seat type the selected
    // package knows about is shown; only `selected` ones are submitted. `selected`
    // is pre-checked for the seats the package entitles by default, and can be
    // toggled to opt into entitling additional seats. `minSeats` is the billing
    // floor (0 = none); `rate` is the per-seat rate, prefilled from the package
    // override default; `commitmentPrice` (optional) creates a prepaid commit of
    // `minSeats * rate` invoiced at that price.
    seats: z
      .record(
        z.string(),
        z
          .object({
            selected: z.boolean().default(false),
            minSeats: z.number().int().min(0),
            rate: z.number().min(0),
            commitmentPrice: z.number().min(0).optional(),
            paymentFrequency: z
              .enum([
                "one_time",
                "monthly",
                "quarterly",
                "semi_annually",
                "annually",
              ])
              .default("one_time"),
            paymentPeriods: z
              .number()
              .int("Number of periods must be a whole number")
              .min(2, "Number of periods must be at least 2")
              .max(60, "Number of periods must be at most 60")
              .optional(),
          })
          .refine(
            (s) =>
              s.paymentFrequency === "one_time" ||
              s.paymentPeriods !== undefined,
            { message: "Periods required when frequency is not one-time" }
          )
      )
      .default({}),
  })
  .refine(
    (s) =>
      !s.showInitialCredits ||
      s.initialCreditsFrequency === "one_time" ||
      s.initialCreditsPeriods !== undefined,
    {
      message: "Periods required when frequency is not one-time",
      path: ["initialCreditsPeriods"],
    }
  );
type SwitchContractFormValues = z.infer<typeof SwitchContractFormSchema>;

type SwitchContractBodyInput = z.input<typeof SwitchContractBodySchema>;

function snapDatetimeLocalToHour(value: string): string {
  if (
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) &&
    value.slice(14, 16) !== "00"
  ) {
    return value.slice(0, 14) + "00";
  }
  return value;
}

const isLegacyPackageName = (name: string) => /\blegacy\b/i.test(name);

const DEFAULT_PERIODS_FOR_FREQUENCY: Record<string, number | undefined> = {
  monthly: 12,
  quarterly: 4,
  semi_annually: 2,
  annually: 1,
  one_time: undefined,
};

interface SwitchContractDialogProps {
  owner: WorkspaceType;
  stripeCustomerId: string | null;
}

export default function SwitchContractDialog({
  owner,
  stripeCustomerId,
}: SwitchContractDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<
    HTMLElement | undefined
  >(undefined);

  useEffect(() => {
    if (typeof document !== "undefined") {
      setPortalContainer(document.body);
    }
  }, []);

  const { plans } = usePokePlans();
  const {
    packages: metronomePackages,
    isPackagesLoading,
    packagesError,
  } = usePokeMetronomePackages({ disabled: !open });
  const router = useAppRouter();

  // Default datetime seeded into the enterprise startingAt field: midnight UTC
  // of the day after today. The operator may freely change it to any moment,
  // including the past — there is no enforced minimum.
  const defaultStartingAtUTC = useMemo(() => {
    const now = new Date();
    const d = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
    );
    const pad = (n: number) => String(n).padStart(2, "0");
    return (
      `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
      `T00:00`
    );
  }, []);

  // No prefilled usage cap: the legacy `programmatic_usage_configuration.paygCapMicroUsd`
  // is for the programmatic-usage / Stripe flow and must not be read here.
  // The credit-priced usage cap lives on `credit_usage_configuration.usageCapCredits`
  // and is managed via the "Manage Credit Usage Configuration" plugin — operators
  // enter the desired cap (in AWU credits) fresh when switching contracts.
  const form = useForm<SwitchContractFormValues>({
    resolver: zodResolver(SwitchContractFormSchema),
    defaultValues: {
      metronomePackageId: "",
      planCode: "",
      hubspotDealId: "",
      startingAt: "",
      startMode: "select",
      stripeCustomerId: stripeCustomerId ?? "",
      stripeCollectionMethod: "charge_automatically",
      netPaymentTermsDays: undefined,
      paygEnabled: false,
      usageCapCredits: undefined,
      defaultDiscountPercent: 0,
      balanceThresholdCredits: undefined,
      defaultPoolCapCredits: undefined,
      programmaticMonthlyCapCredits: undefined,
      autoSeatUpgradeEnabled: false,
      topUpEnabled: false,
      autoInvoiceFinalizationEnabled: true,
      showInitialCredits: false,
      initialCreditsAmount: undefined,
      initialCreditsInvoiceAmount: undefined,
      initialCreditsFrequency: "one_time",
      initialCreditsPeriods: undefined,
      seats: {},
    },
  });

  const watchedStripeCustomerId = form.watch("stripeCustomerId");
  const trimmedStripeCustomerId = watchedStripeCustomerId.trim() || null;
  const {
    currency: resolvedCurrency,
    isCurrencyLoading,
    currencyError,
  } = usePokeStripeCustomerCurrency({
    stripeCustomerId: trimmedStripeCustomerId,
    disabled: !open,
  });

  // Fetch the existing credit config so we pre-populate form fields with
  // current values rather than schema defaults, avoiding accidental overwrites.
  const { asyncArgs: existingCreditConfig } = usePokePluginAsyncArgs({
    pluginId: "manage-credit-usage-configuration",
    pluginResourceTarget: {
      resourceType: "workspaces",
      resourceId: owner.sId,
      workspace: owner,
    },
    disabled: !open,
  });
  const creditConfigAppliedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      creditConfigAppliedRef.current = false;
      return;
    }
    if (!existingCreditConfig || creditConfigAppliedRef.current) {
      return;
    }
    creditConfigAppliedRef.current = true;
    const parsed =
      CreditUsageConfigurationSchema.safeParse(existingCreditConfig);
    if (!parsed.success) {
      return;
    }
    const d = parsed.data;
    // The plugin uses 0 for "no cap/limit"; the form uses undefined.
    const orUndefined = (n: number) => (n > 0 ? n : undefined);
    form.setValue("defaultDiscountPercent", d.defaultDiscountPercent);
    form.setValue("paygEnabled", d.paygEnabled);
    form.setValue("usageCapCredits", orUndefined(d.usageCapCredits));
    form.setValue(
      "balanceThresholdCredits",
      orUndefined(d.balanceThresholdCredits)
    );
    form.setValue(
      "defaultPoolCapCredits",
      orUndefined(d.defaultPoolCapCredits)
    );
    form.setValue(
      "programmaticMonthlyCapCredits",
      orUndefined(d.programmaticMonthlyCapCredits)
    );
    form.setValue("autoSeatUpgradeEnabled", d.autoSeatUpgradeEnabled);
    form.setValue("topUpEnabled", d.topUpEnabled);
    form.setValue(
      "autoInvoiceFinalizationEnabled",
      d.autoInvoiceFinalizationEnabled
    );
  }, [open, existingCreditConfig, form]);

  // Split packages into Current vs Legacy sections (name contains "legacy",
  // case-insensitive). Each section preserves the lib-side sort order.
  // Free-tier packages are currency-agnostic (price is 0) and surface
  // regardless of the resolved Stripe currency.
  const packageGroups = useMemo(() => {
    const visible = metronomePackages.filter(
      (p) => p.tier === "free" || p.currency === resolvedCurrency
    );
    const toOption = (p: (typeof visible)[number]) => ({
      value: p.id,
      display:
        p.tier === "free"
          ? `${p.name} (${p.tier})`
          : `${p.name} (${p.tier}, ${p.currency.toUpperCase()})`,
    });
    return [
      {
        label: "Current",
        options: visible
          .filter((p) => !isLegacyPackageName(p.name))
          .map(toOption),
      },
      // We hide the legacy ones as there is no use case switching to them (at least for now).
      // {
      //   label: "Legacy",
      //   options: inCurrency.filter((p) => isLegacy(p.name)).map(toOption),
      // },
    ];
  }, [metronomePackages, resolvedCurrency]);

  const selectedPackageId = form.watch("metronomePackageId");
  const selectedPackage = useMemo(
    () => metronomePackages.find((p) => p.id === selectedPackageId),
    [metronomePackages, selectedPackageId]
  );
  const selectedTier = selectedPackage?.tier ?? null;
  const selectedName = selectedPackage?.name ?? null;
  const selectedSeats = useMemo(
    () => selectedPackage?.seats ?? [],
    [selectedPackage]
  );

  // Reset the seat settings to the seats of the newly selected package: seats
  // the package entitles are pre-selected, the rest are shown unchecked for the
  // operator to opt into. Min seats defaults to 0, the rate is prefilled from
  // the package override default. The default is in Metronome's fiat unit (cents
  // for USD, whole units for EUR); the dialog works in major units
  // (dollars/euros), so convert for display. Avoids stale values leaking across
  // package selections.
  useEffect(() => {
    const next: Record<
      string,
      {
        selected: boolean;
        minSeats: number;
        rate: number;
        paymentFrequency: "one_time";
      }
    > = {};
    for (const seat of selectedSeats) {
      const rate =
        seat.defaultRate != null && resolvedCurrency
          ? amountCents(seat.defaultRate, resolvedCurrency) / 100
          : (seat.defaultRate ?? 0);
      next[seat.seatType] = {
        selected: seat.entitled,
        minSeats: 0,
        rate,
        paymentFrequency: "one_time",
      };
    }
    form.setValue("seats", next);
  }, [selectedSeats, form, resolvedCurrency]);

  // Clear a stale package selection when the resolved currency changes so a
  // previously-picked package can't survive a currency switch silently. Free
  // packages are currency-agnostic and exempt from this check.
  useEffect(() => {
    if (
      resolvedCurrency &&
      selectedPackage &&
      selectedPackage.tier !== "free" &&
      selectedPackage.currency !== resolvedCurrency
    ) {
      form.setValue("metronomePackageId", "");
    }
  }, [resolvedCurrency, selectedPackage, form]);

  // When the operator picks a package, derive (Pro/Business) or reset
  // (Enterprise) the plan code. The full list of ENT_* plans is offered for
  // enterprise; pro/business map to a single plan code. PAYG is force-disabled
  // for tiers that don't support it (currently: pro).
  useEffect(() => {
    if (selectedTier === "pro") {
      if (isLegacyPackageName(selectedName ?? "")) {
        form.setValue("planCode", PRO_PLAN_SEAT_29_CODE);
      } else {
        assert("There is no non-legacy pro plan");
      }
      form.setValue("startingAt", "");
      form.setValue("startMode", "select");
    } else if (selectedTier === "business") {
      if (isLegacyPackageName(selectedName ?? "")) {
        form.setValue("planCode", PRO_PLAN_SEAT_39_CODE);
      } else {
        form.setValue("planCode", CREDIT_PRICED_BUSINESS_PLAN_CODE);
      }
      form.setValue("startingAt", "");
      form.setValue("startMode", "select");
    } else if (selectedTier === "enterprise") {
      form.setValue("planCode", "");
      form.setValue("startingAt", defaultStartingAtUTC);
      form.setValue("startMode", "select");
    } else if (selectedTier === "free") {
      form.setValue("planCode", CREDIT_PRICED_FREE_PLAN_CODE);
      form.setValue("startingAt", "");
      form.setValue("startMode", "select");
    }
    if (selectedTier && !isPaygEligibleTier(selectedTier)) {
      form.setValue("paygEnabled", false);
      form.setValue("usageCapCredits", undefined);
    }
  }, [selectedTier, selectedName, form, defaultStartingAtUTC]);

  const startMode = form.watch("startMode");
  const startingAt = form.watch("startingAt");
  const startingAtLocalLabel = useMemo(() => {
    if (!startingAt) {
      return null;
    }
    const d = new Date(startingAt + ":00Z");
    if (isNaN(d.getTime())) {
      return null;
    }
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }, [startingAt]);

  // 1st of the current month at 00:00 UTC — the "retroactive" anchor. Computed
  // as an ISO string sent verbatim to the server (no datetime-local conversion).
  const { retroactiveFirstOfMonthISO, retroactiveFirstOfMonthLabel } =
    useMemo(() => {
      const now = new Date();
      const d = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0)
      );
      return {
        retroactiveFirstOfMonthISO: d.toISOString(),
        retroactiveFirstOfMonthLabel: d.toUTCString(),
      };
    }, []);

  const startModeOptions = useMemo(
    () => [
      { value: "immediately", display: "Start immediately" },
      {
        value: "retroactive_first_of_month",
        display: `Start retroactively on ${retroactiveFirstOfMonthLabel}`,
      },
      { value: "select", display: "Select start time" },
    ],
    [retroactiveFirstOfMonthLabel]
  );

  const enterprisePlanOptions = useMemo(
    () =>
      plans
        .filter(
          (plan) =>
            isEnterprisePlanPrefix(plan.code) && isCreditPricedPlan(plan)
        )
        .map((plan) => ({
          value: plan.code,
          display: `${plan.name} (${plan.code})`,
        })),
    [plans]
  );

  const paygEnabled = form.watch("paygEnabled");
  const paygEligible =
    selectedTier !== null && isPaygEligibleTier(selectedTier);
  const autoSeatUpgradeEnabled = form.watch("autoSeatUpgradeEnabled");
  const topUpEnabled = form.watch("topUpEnabled");
  const autoInvoiceFinalizationEnabled = form.watch(
    "autoInvoiceFinalizationEnabled"
  );

  const showInitialCredits = form.watch("showInitialCredits");
  const initialCreditsFrequency = form.watch("initialCreditsFrequency");

  const stripeCollectionMethod = form.watch("stripeCollectionMethod");
  const watchedSeats = form.watch("seats");

  // When any payment frequency field changes, pre-fill its sibling periods
  // field with the canonical default. Uses form.watch(callback) — the only
  // reliable way to know exactly which field changed (via the `name` argument).
  useEffect(() => {
    const { unsubscribe } = form.watch((value, { name }) => {
      if (name === "initialCreditsFrequency") {
        form.setValue(
          "initialCreditsPeriods",
          DEFAULT_PERIODS_FOR_FREQUENCY[
            value.initialCreditsFrequency ?? "one_time"
          ]
        );
      }
      if (name?.startsWith("seats.") && name.endsWith(".paymentFrequency")) {
        const seatType = name.split(".")[1];
        const freq = value.seats?.[seatType]?.paymentFrequency ?? "one_time";
        form.setValue(
          `seats.${seatType}.paymentPeriods`,
          DEFAULT_PERIODS_FOR_FREQUENCY[freq]
        );
      }
    });
    return unsubscribe;
  }, [form]);

  const onSubmit = useCallback(
    (values: SwitchContractFormValues) => {
      const trimmedStripe = values.stripeCustomerId.trim();
      const trimmedHubspotDealId = values.hubspotDealId?.trim();
      const cleaned: SwitchContractBodyInput = {
        metronomePackageId: values.metronomePackageId.trim(),
        planCode: values.planCode.trim(),
        paygEnabled: values.paygEnabled,
        ...(trimmedHubspotDealId
          ? { hubspotDealId: trimmedHubspotDealId }
          : {}),
      };
      // For free-tier switches, the operator can omit the Stripe customer —
      // the resulting Metronome contract has no Stripe billing link. The
      // collection method only matters when a Stripe customer is wired in.
      if (trimmedStripe) {
        cleaned.stripeCustomerId = trimmedStripe;
        cleaned.stripeCollectionMethod = values.stripeCollectionMethod;
      }
      if (values.netPaymentTermsDays !== undefined) {
        cleaned.netPaymentTermsDays = values.netPaymentTermsDays;
      }
      if (values.usageCapCredits !== undefined) {
        cleaned.usageCapCredits = values.usageCapCredits;
      }
      cleaned.defaultDiscountPercent = values.defaultDiscountPercent;
      cleaned.autoSeatUpgradeEnabled = values.autoSeatUpgradeEnabled;
      cleaned.topUpEnabled = values.topUpEnabled;
      cleaned.autoInvoiceFinalizationEnabled =
        values.autoInvoiceFinalizationEnabled;
      if (values.balanceThresholdCredits !== undefined) {
        cleaned.balanceThresholdCredits = values.balanceThresholdCredits;
      }
      if (values.defaultPoolCapCredits !== undefined) {
        cleaned.defaultPoolCapCredits = values.defaultPoolCapCredits;
      }
      if (values.programmaticMonthlyCapCredits !== undefined) {
        cleaned.programmaticMonthlyCapCredits =
          values.programmaticMonthlyCapCredits;
      }
      // Initial credits: only sent when the operator toggled the section on.
      // Both the credit amount and the invoice amount are then required, and a
      // Stripe customer must be present to invoice against.
      if (values.showInitialCredits) {
        if (
          values.initialCreditsAmount === undefined ||
          values.initialCreditsInvoiceAmount === undefined
        ) {
          setError(
            "Initial credits require both a credit amount and an invoice amount."
          );
          return;
        }
        if (!trimmedStripe) {
          setError("Initial credits require a Stripe customer to invoice.");
          return;
        }
        if (
          values.initialCreditsFrequency !== "one_time" &&
          (values.initialCreditsPeriods === undefined ||
            values.initialCreditsPeriods < 2)
        ) {
          setError(
            "Scheduled payments require a number of periods of at least 2."
          );
          return;
        }
        cleaned.initialCredits = {
          amountCredits: values.initialCreditsAmount,
          invoiceAmount: values.initialCreditsInvoiceAmount,
          paymentSchedule: {
            frequency: values.initialCreditsFrequency,
            periods:
              values.initialCreditsFrequency !== "one_time"
                ? values.initialCreditsPeriods
                : undefined,
          },
        };
      }
      // Resolve the start moment for enterprise. "immediately" leaves
      // `startingAt` unset so the server swaps at the current hour.
      if (selectedTier === "enterprise") {
        if (values.startMode === "retroactive_first_of_month") {
          cleaned.startingAt = retroactiveFirstOfMonthISO;
        } else if (values.startMode === "select" && values.startingAt) {
          // datetime-local has no timezone — append Z to interpret as UTC.
          cleaned.startingAt = new Date(
            values.startingAt + ":00Z"
          ).toISOString();
        }
      }
      // Seats: every seat the package knows about, each carrying its `selected`
      // state, so the server can entitle checked seats and disable unchecked
      // ones the package would otherwise sell. Entitled-by-default seats are
      // pre-checked. A checked seat the package does not entitle requires a
      // positive rate (except the free seat, which may be entitled at rate 0).
      const seats: SwitchContractBodyInput["seats"] = [];
      for (const { seatType, entitled } of selectedSeats) {
        const entry = values.seats?.[seatType];
        const selected = entry?.selected ?? false;
        const minSeats = Number.isFinite(entry?.minSeats)
          ? (entry?.minSeats ?? 0)
          : 0;
        const rate = Number.isFinite(entry?.rate) ? (entry?.rate ?? 0) : 0;
        // If the operator left commitment price blank, default to minSeats * rate
        // (the list value of the committed seats).
        const explicitPrice =
          typeof entry?.commitmentPrice === "number" &&
          Number.isFinite(entry.commitmentPrice) &&
          entry.commitmentPrice > 0
            ? entry.commitmentPrice
            : null;
        const commitmentPrice =
          explicitPrice ??
          (minSeats > 0 && rate > 0 ? minSeats * rate : undefined);
        const paymentFrequency = entry?.paymentFrequency ?? "one_time";
        const paymentPeriods = entry?.paymentPeriods;
        if (selected && !entitled && seatType !== "free" && !(rate > 0)) {
          setError(
            `Seat "${seatType}" is not entitled by the selected package and ` +
              "requires a rate greater than 0 to entitle it."
          );
          return;
        }
        if (
          commitmentPrice !== undefined &&
          paymentFrequency !== "one_time" &&
          (paymentPeriods === undefined || paymentPeriods < 2)
        ) {
          setError(
            `Seat "${seatType}" has a scheduled payment frequency but no valid ` +
              "number of periods (minimum 2)."
          );
          return;
        }
        seats.push({
          seatType,
          selected,
          minSeats,
          rate,
          commitmentPrice,
          paymentSchedule: {
            frequency: paymentFrequency,
            periods:
              paymentFrequency !== "one_time" ? paymentPeriods : undefined,
          },
        });
      }
      if (seats.length > 0) {
        cleaned.seats = seats;
      }

      const submit = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
          const r = await clientFetch(
            `/api/poke/workspaces/${owner.sId}/switch_contract`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(cleaned),
            }
          );
          if (!r.ok) {
            throw new Error(
              `Something went wrong: ${r.status} ${await r.text()}`
            );
          }
          form.reset();
          setOpen(false);
          router.reload();
        } catch (e) {
          setIsSubmitting(false);
          if (e instanceof Error) {
            setError(e.message);
          }
        }
      };
      void submit();
    },
    [
      form,
      owner.sId,
      router,
      selectedTier,
      selectedSeats,
      retroactiveFirstOfMonthISO,
    ]
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" label="🔁 Switch contract" />
      </DialogTrigger>
      <DialogContent className="bg-primary-50 dark:bg-primary-50-night sm:h-[90vh] sm:max-w-[860px]">
        <DialogHeader>
          <DialogTitle>Switch contract for {owner.name}</DialogTitle>
          <DialogDescription>
            Pick the Metronome package and target plan. Enterprise packages
            require a start time at least one hour in the future; Pro and
            Business packages swap at the current hour.
          </DialogDescription>
        </DialogHeader>
        {isSubmitting ? (
          <DialogContainer>
            <div className="flex justify-center">
              <Spinner size="lg" />
            </div>
          </DialogContainer>
        ) : (
          <PokeForm {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              style={{ display: "contents" }}
            >
              <DialogContainer>
                <div className="grid grid-cols-[200px_1fr] items-center gap-x-4 gap-y-2">
                  {error && (
                    <div className="col-span-2 text-warning">{error}</div>
                  )}
                  <Label className="text-sm">
                    Stripe Customer ID
                    <span className="ml-1 text-muted-foreground dark:text-muted-foreground-night">
                      (optional)
                    </span>
                  </Label>
                  <InputField
                    control={form.control}
                    name="stripeCustomerId"
                    hideLabel
                    placeholder="cus_1234567890"
                  />
                  <Label className="text-sm">
                    HubSpot Deal ID
                    <span className="ml-1 text-muted-foreground dark:text-muted-foreground-night">
                      (optional)
                    </span>
                  </Label>
                  <InputField
                    control={form.control}
                    name="hubspotDealId"
                    hideLabel
                    placeholder="e.g., 12345678901"
                  />
                  {isCurrencyLoading && (
                    <div className="col-span-2 flex items-center gap-2 text-sm text-muted-foreground dark:text-muted-foreground-night">
                      <Spinner size="sm" />
                      <span>Resolving customer currency...</span>
                    </div>
                  )}
                  {currencyError && (
                    <div className="col-span-2 text-sm text-warning">
                      Failed to resolve currency from Stripe customer:{" "}
                      {currencyError.message}
                    </div>
                  )}
                  {trimmedStripeCustomerId && (
                    <>
                      <Label className="text-sm">Collection method</Label>
                      <SelectField
                        control={form.control}
                        name="stripeCollectionMethod"
                        hideLabel
                        mountPortalContainer={portalContainer}
                        options={[
                          {
                            value: "charge_automatically",
                            display: "Charge automatically (card on file)",
                          },
                          {
                            value: "send_invoice",
                            display: "Send invoice (manual payment)",
                          },
                        ]}
                      />
                    </>
                  )}
                  {trimmedStripeCustomerId &&
                    stripeCollectionMethod === "send_invoice" && (
                      <>
                        <Label className="text-sm">
                          Net payment terms (days)
                        </Label>
                        <InputField
                          control={form.control}
                          name="netPaymentTermsDays"
                          hideLabel
                          type="number"
                          placeholder="Metronome account default"
                        />
                      </>
                    )}
                  {isPackagesLoading && (
                    <div className="col-span-2 flex items-center gap-2 text-sm text-muted-foreground dark:text-muted-foreground-night">
                      <Spinner size="sm" />
                      <span>Loading Metronome packages...</span>
                    </div>
                  )}
                  {!isPackagesLoading && packagesError && (
                    <div className="col-span-2 text-sm text-warning">
                      Failed to load Metronome packages: {packagesError.message}
                    </div>
                  )}
                  {!isPackagesLoading &&
                    !packagesError &&
                    !isCurrencyLoading &&
                    (resolvedCurrency || !trimmedStripeCustomerId) && (
                      <>
                        <Label className="text-sm">
                          Package
                          {resolvedCurrency &&
                            ` (${resolvedCurrency.toUpperCase()})`}
                        </Label>
                        <SelectField
                          control={form.control}
                          name="metronomePackageId"
                          hideLabel
                          mountPortalContainer={portalContainer}
                          groups={packageGroups}
                        />
                      </>
                    )}
                  {selectedTier === "enterprise" && (
                    <>
                      <Label className="text-sm">Enterprise plan</Label>
                      <SelectField
                        control={form.control}
                        name="planCode"
                        hideLabel
                        mountPortalContainer={portalContainer}
                        options={enterprisePlanOptions}
                      />
                      <Label className="text-sm">Start</Label>
                      <SelectField
                        control={form.control}
                        name="startMode"
                        hideLabel
                        mountPortalContainer={portalContainer}
                        options={startModeOptions}
                      />
                      {startMode === "select" && (
                        <>
                          <Label className="text-sm">Starts at (UTC)</Label>
                          <div className="relative">
                            <InputField
                              control={form.control}
                              name="startingAt"
                              hideLabel
                              type="datetime-local"
                              step={3600}
                              transformValue={snapDatetimeLocalToHour}
                            />
                            {startingAtLocalLabel && (
                              <p className="mt-1 text-xs text-muted-foreground dark:text-muted-foreground-night absolute top-2 right-2">
                                Local: {startingAtLocalLabel}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                  {(selectedTier === "pro" ||
                    selectedTier === "business" ||
                    selectedTier === "free") && (
                    <div className="col-span-2 text-sm text-muted-foreground dark:text-muted-foreground-night">
                      Target plan:{" "}
                      <span className="font-mono">
                        {form.watch("planCode")}
                      </span>{" "}
                      — swap at the current hour, subscription flips
                      synchronously.
                    </div>
                  )}
                </div>
                {selectedTier && selectedTier !== "free" && (
                  <div className="border-t pt-4">
                    <Label className="mb-3 block text-sm font-medium">
                      Credit configuration
                    </Label>
                    <div className="grid grid-cols-[200px_1fr] items-center gap-x-4 gap-y-2">
                      {paygEligible && (
                        <>
                          <Label className="text-sm">Pay-as-you-go</Label>
                          <SliderToggle
                            selected={paygEnabled}
                            onClick={() =>
                              form.setValue("paygEnabled", !paygEnabled)
                            }
                          />
                        </>
                      )}
                      <Label className="text-sm">Monthly usage cap (AWU)</Label>
                      <InputField
                        control={form.control}
                        name="usageCapCredits"
                        hideLabel
                        type="number"
                        placeholder="no cap"
                      />
                      <Label className="text-sm">Default discount (%)</Label>
                      <InputField
                        control={form.control}
                        name="defaultDiscountPercent"
                        hideLabel
                        type="number"
                        placeholder="0"
                      />
                      <Label className="text-sm">Balance alert (AWU)</Label>
                      <InputField
                        control={form.control}
                        name="balanceThresholdCredits"
                        hideLabel
                        type="number"
                        placeholder="no alert"
                      />
                      <Label className="text-sm">
                        Per-user pool access (AWU)
                      </Label>
                      <InputField
                        control={form.control}
                        name="defaultPoolCapCredits"
                        hideLabel
                        type="number"
                        placeholder="no access"
                      />
                      <Label className="text-sm">
                        Programmatic cap/mo (AWU)
                      </Label>
                      <InputField
                        control={form.control}
                        name="programmaticMonthlyCapCredits"
                        hideLabel
                        type="number"
                        placeholder="no cap"
                      />
                      <Label className="text-sm">Auto-upgrade seats</Label>
                      <SliderToggle
                        selected={autoSeatUpgradeEnabled}
                        onClick={() =>
                          form.setValue(
                            "autoSeatUpgradeEnabled",
                            !autoSeatUpgradeEnabled
                          )
                        }
                      />
                      <Label className="text-sm">Top-up (Enterprise)</Label>
                      <SliderToggle
                        selected={topUpEnabled}
                        onClick={() =>
                          form.setValue("topUpEnabled", !topUpEnabled)
                        }
                      />
                      <Label className="text-sm">
                        Auto invoice finalization
                      </Label>
                      <SliderToggle
                        selected={autoInvoiceFinalizationEnabled}
                        onClick={() =>
                          form.setValue(
                            "autoInvoiceFinalizationEnabled",
                            !autoInvoiceFinalizationEnabled
                          )
                        }
                      />
                    </div>
                  </div>
                )}
                {selectedSeats.length > 0 && (
                  <div className="space-y-2 border-t pt-4">
                    <Label className="text-sm">
                      Seats configuration{" "}
                      {resolvedCurrency
                        ? `(rate & price in ${resolvedCurrency.toUpperCase()})`
                        : ""}
                    </Label>
                    <div className="text-xs text-muted-foreground dark:text-muted-foreground-night">
                      Checked seats are entitled on the new contract. Seats the
                      package does not entitle by default are unchecked — check
                      one to entitle it (a non-zero rate is required, except for
                      the free seat).
                    </div>
                    {/* Header row */}
                    <div className="flex items-center gap-3 pb-1 text-xs font-medium text-muted-foreground dark:text-muted-foreground-night">
                      <div className="w-32 shrink-0" />
                      <div className="flex-1">Min seats</div>
                      <div className="flex-1">
                        Seat rate
                        {resolvedCurrency
                          ? ` (${resolvedCurrency.toUpperCase()})`
                          : ""}
                      </div>
                      <div className="flex-1">
                        Commitment price
                        {resolvedCurrency
                          ? ` (${resolvedCurrency.toUpperCase()})`
                          : ""}
                      </div>
                      <div className="flex-1">Payment schedule</div>
                      <div className="flex-1">Periods</div>
                    </div>
                    {selectedSeats.map(({ seatType, entitled }) => {
                      const isSelected =
                        watchedSeats?.[seatType]?.selected ?? false;
                      const seatPaymentFrequency =
                        watchedSeats?.[seatType]?.paymentFrequency ??
                        "one_time";
                      const minSeats = watchedSeats?.[seatType]?.minSeats ?? 0;
                      const rate = watchedSeats?.[seatType]?.rate ?? 0;
                      const isAnnualSeat = seatType.endsWith("_yearly");
                      const defaultCommitment =
                        minSeats > 0 && rate > 0 ? minSeats * rate : null;
                      const monthlyRate =
                        isAnnualSeat && rate > 0 ? rate / 12 : null;
                      return (
                        <div key={seatType} className="flex items-center gap-3">
                          <div className="flex w-32 shrink-0 items-center gap-2">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) =>
                                form.setValue(
                                  `seats.${seatType}.selected`,
                                  checked === true
                                )
                              }
                            />
                            <span className="font-mono text-sm">
                              {seatType}
                              {!entitled && (
                                <span className="ml-1 text-muted-foreground dark:text-muted-foreground-night">
                                  *
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="flex-1">
                            <InputField
                              control={form.control}
                              name={`seats.${seatType}.minSeats`}
                              hideLabel
                              type="number"
                              placeholder="0"
                              disabled={!isSelected}
                            />
                          </div>
                          <div className="flex-1 relative">
                            <InputField
                              control={form.control}
                              name={`seats.${seatType}.rate`}
                              hideLabel
                              type="number"
                              placeholder="0"
                              disabled={!isSelected}
                            />
                            {isSelected && monthlyRate !== null && (
                              <p className="mt-0.5 text-xs text-muted-foreground dark:text-muted-foreground-night absolute top-2 right-2">
                                {monthlyRate % 1 === 0
                                  ? monthlyRate
                                  : monthlyRate.toFixed(2)}
                                /mo
                              </p>
                            )}
                          </div>
                          <div className="flex-1">
                            <InputField
                              control={form.control}
                              name={`seats.${seatType}.commitmentPrice`}
                              hideLabel
                              type="number"
                              placeholder={
                                defaultCommitment !== null
                                  ? String(defaultCommitment)
                                  : "optional"
                              }
                              disabled={!isSelected}
                            />
                          </div>
                          <div className="flex-1">
                            <SelectField
                              control={form.control}
                              name={`seats.${seatType}.paymentFrequency`}
                              hideLabel
                              mountPortalContainer={portalContainer}
                              options={[
                                { value: "one_time", display: "One-time" },
                                { value: "monthly", display: "Monthly" },
                                { value: "quarterly", display: "Quarterly" },
                                {
                                  value: "semi_annually",
                                  display: "Semi-annually",
                                },
                                { value: "annually", display: "Annually" },
                              ]}
                            />
                          </div>
                          <div className="flex-1">
                            {seatPaymentFrequency !== "one_time" && (
                              <InputField
                                control={form.control}
                                name={`seats.${seatType}.paymentPeriods`}
                                hideLabel
                                type="number"
                                placeholder="e.g., 4"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {trimmedStripeCustomerId && resolvedCurrency && (
                  <div className="border-t pt-4">
                    <div className="grid grid-cols-[200px_1fr] items-center gap-x-4 gap-y-2">
                      <Label className="text-sm font-medium">
                        Initial credits (prepaid commit)
                      </Label>
                      <SliderToggle
                        selected={showInitialCredits}
                        onClick={() =>
                          form.setValue(
                            "showInitialCredits",
                            !showInitialCredits
                          )
                        }
                      />
                      {showInitialCredits && (
                        <>
                          <Label className="text-sm">Credits (AWU)</Label>
                          <InputField
                            control={form.control}
                            name="initialCreditsAmount"
                            hideLabel
                            type="number"
                            placeholder="e.g., 100000"
                          />
                          <Label className="text-sm">
                            Invoice ({resolvedCurrency.toUpperCase()})
                          </Label>
                          <InputField
                            control={form.control}
                            name="initialCreditsInvoiceAmount"
                            hideLabel
                            type="number"
                            placeholder="e.g., 5000"
                          />
                          <Label className="text-sm">Payment schedule</Label>
                          <SelectField
                            control={form.control}
                            name="initialCreditsFrequency"
                            hideLabel
                            mountPortalContainer={portalContainer}
                            options={[
                              {
                                value: "one_time",
                                display: "One-time",
                              },
                              { value: "monthly", display: "Monthly" },
                              {
                                value: "quarterly",
                                display: "Quarterly",
                              },
                              {
                                value: "semi_annually",
                                display: "Semi-annually",
                              },
                              { value: "annually", display: "Annually" },
                            ]}
                          />
                          {initialCreditsFrequency !== "one_time" && (
                            <>
                              <Label className="text-sm">Periods</Label>
                              <InputField
                                control={form.control}
                                name="initialCreditsPeriods"
                                hideLabel
                                type="number"
                                placeholder="e.g., 4"
                              />
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </DialogContainer>
              <DialogFooter>
                <Button
                  type="submit"
                  variant="warning"
                  label="Switch"
                  disabled={
                    !selectedTier ||
                    (selectedTier !== "free" && !resolvedCurrency)
                  }
                />
              </DialogFooter>
            </form>
          </PokeForm>
        )}
      </DialogContent>
    </Dialog>
  );
}
